import type { LangCode } from "@scoach/types";
import { Play, Stop } from "@scoach/ui/icons";
import { useEffect, useRef, useState } from "react";

import { api } from "../lib/http.ts";

interface ReadAloudButtonProps {
  /** Resolve the text to read when clicked (so it's always current). */
  getText: () => string;
  /** Language to synthesize in. Omit to let the backend detect from the text. */
  lang?: LangCode;
  label?: string;
  className?: string;
}

/**
 * Speaker button that reads text aloud via the backend /tts endpoint (Google
 * Cloud Text-to-Speech). Click to play, click again to stop. Used on the
 * interview + meeting summary screens.
 */
export function ReadAloudButton({
  getText,
  lang,
  label = "Read aloud",
  className,
}: ReadAloudButtonProps) {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  function cleanup() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlaying(false);
  }

  // Stop playback + free the blob URL on unmount.
  useEffect(() => cleanup, []);

  async function toggle() {
    if (playing) {
      cleanup();
      return;
    }
    const text = getText().trim();
    if (!text) return;
    setBusy(true);
    try {
      const { audioBase64, mime } = await api<{ audioBase64: string; mime: string }>("/tts", {
        method: "POST",
        body: { text, lang },
      });
      const bytes = Uint8Array.from(atob(audioBase64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = cleanup;
      audio.onerror = cleanup;
      await audio.play();
      setPlaying(true);
    } catch (err) {
      alert(`Read aloud failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={className ?? "pill-btn"}
      onClick={toggle}
      disabled={busy}
      title={label}
      aria-label={label}
    >
      {playing ? <Stop size={14} /> : <Play size={14} />} {busy ? "…" : playing ? "Stop" : label}
    </button>
  );
}
